import type { HelpGuide } from '../types';

export const settingsUsersGuide: HelpGuide = {
  key: 'settings-users',
  title: 'ผู้ใช้ & บทบาท (Users)',
  subtitle: 'เมนู: ระบบ ▸ ผู้ใช้ & บทบาท',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/settings-users/คู่มือการใช้งาน_ผู้ใช้และบทบาท_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ผู้ใช้ & บทบาท” ใช้บริหารจัดการผู้ใช้งานระบบ DCC ERP ทั้งหมด — เพิ่มผู้ใช้ใหม่จาก Entra Directory ของบริษัท, กำหนดว่าแต่ละคนเข้าถึงบริษัทใดได้บ้าง (DCC/TTOP/RCI), ตั้งบทบาท (Role) เพื่อให้สิทธิ์การใช้งาน, แก้ไขข้อมูลผู้ใช้ และเปิด/ปิดการใช้งานบัญชี',
        },
        {
          kind: 'callout',
          text: 'แนวคิดสำคัญ — ผู้ใช้ในระบบมาจากบัญชี Microsoft Entra (อีเมลบริษัท) เท่านั้น จึงต้อง “ค้นหาจาก directory” แทนการพิมพ์อีเมลเอง · สิทธิ์การใช้งานมาจาก “บทบาท” ไม่ได้ตั้งทีละเมนู · บทบาทมี 2 แบบ คือ บทบาท global (ใช้ได้ทุกบริษัท) และ บทบาทเฉพาะบริษัท (ผูกกับบริษัทเดียว)',
          variant: 'info',
        },
        { kind: 'para', text: 'สิ่งที่ทำได้ในหน้านี้:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ดูรายชื่อผู้ใช้ทั้งหมด — ',
              text: 'พร้อมสถานะ, จำนวนบริษัทที่เข้าถึง และบทบาท global',
            },
            {
              lead: 'เพิ่มผู้ใช้ใหม่ — ',
              text: 'ค้นหาจาก Entra Directory แล้วกำหนดบริษัทและบทบาทเริ่มต้น',
            },
            {
              lead: 'แก้ไขผู้ใช้ — ',
              text: 'แก้ชื่อแสดง, บริษัทที่เข้าถึง, บริษัทเริ่มต้น และสถานะ',
            },
            { lead: 'ปิด/เปิดการใช้งาน — ', text: 'ระงับหรือคืนสิทธิ์การเข้าใช้ระบบของผู้ใช้' },
            {
              lead: 'กำหนดบทบาทเฉพาะบริษัท — ',
              text: 'ตั้งบทบาทของผู้ใช้สำหรับบริษัทที่กำลังทำงานอยู่',
            },
          ],
        },
        {
          kind: 'callout',
          text: 'หน้านี้สงวนไว้สำหรับผู้ดูแลระบบ (สิทธิ์ user.manage) เท่านั้น การเพิ่ม/แก้ไข/ปิดผู้ใช้และการกำหนดบทบาทมีผลต่อสิทธิ์เข้าถึงข้อมูลทั้งระบบ โปรดทำด้วยความระมัดระวัง',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/settings-users/01-users-list.png',
          caption:
            'รายชื่อผู้ใช้ทั้งหมด — อีเมล, ชื่อ, สถานะ (ใช้งาน/ปิด), จำนวนบริษัทที่เข้าถึง, บทบาท global และปุ่มจัดการรายแถว',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        {
          kind: 'para',
          text: 'ส่วนบนเป็นหัวเรื่อง “ผู้ใช้ & บทบาท” พร้อมปุ่ม + เพิ่มผู้ใช้ (มุมขวา) สำหรับเปิดหน้าต่างเพิ่มผู้ใช้ใหม่ ส่วนล่างเป็นตารางรายชื่อผู้ใช้',
        },
        { kind: 'para', text: 'คอลัมน์ในตารางรายชื่อผู้ใช้:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'อีเมล — ', text: 'อีเมลบริษัท (Entra) ของผู้ใช้ ใช้เป็นตัวระบุตัวตนหลัก' },
            { lead: 'ชื่อ — ', text: 'ชื่อแสดงของผู้ใช้' },
            {
              lead: 'สถานะ — ',
              text: 'ใช้งาน (เขียว) หรือ ปิด (แดง) บอกว่าบัญชีนี้เข้าใช้ระบบได้หรือไม่',
            },
            { lead: 'บริษัทที่เข้าถึง — ', text: 'จำนวนบริษัทที่ผู้ใช้เข้าถึงได้ เช่น “2 บริษัท”' },
            {
              lead: 'บทบาท global — ',
              text: 'บทบาทที่ใช้ได้ทุกบริษัท (คั่นด้วยจุลภาค) ถ้าไม่มีจะแสดง —',
            },
          ],
        },
        { kind: 'para', text: 'ปุ่มจัดการในแต่ละแถว:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'แก้ไข — ', text: 'เปิดหน้าต่างแก้ไขข้อมูลผู้ใช้' },
            {
              lead: 'ปิด / เปิด — ',
              text: 'สลับสถานะการใช้งาน (แสดง “ปิด” เมื่อกำลังใช้งาน, แสดง “เปิด” เมื่อถูกปิดอยู่)',
            },
            {
              lead: 'แก้บทบาท — ',
              text: 'กำหนดบทบาทเฉพาะบริษัทที่กำลังทำงานอยู่ (ปุ่มนี้แสดงเมื่อผู้ใช้อยู่ในบริษัทที่เลือกเท่านั้น)',
            },
          ],
        },
      ],
    },
    {
      id: 'add-search',
      heading: '3. การเพิ่มผู้ใช้ใหม่ — ค้นหาจาก Entra Directory',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม + เพิ่มผู้ใช้ ระบบจะเปิดหน้าต่าง “เพิ่มผู้ใช้ใหม่” พร้อมคำอธิบาย “ค้นหาผู้ใช้จาก Entra directory แล้วกำหนดสิทธิ์” ในช่อง ค้นหาจาก Entra Directory ให้พิมพ์ชื่อหรืออีเมล (อย่างน้อย 2 ตัวอักษร) ระบบจะค้นหาจากไดเรกทอรีของบริษัทและแสดงรายการผลลัพธ์ใต้ช่องค้นหา',
        },
        {
          kind: 'para',
          text: 'แต่ละรายการในผลลัพธ์จะแสดงชื่อ (ตัวหนา) และอีเมล/บัญชีผู้ใช้ (สีจาง) คลิกที่รายชื่อเพื่อเลือกผู้ใช้คนนั้น',
        },
        {
          kind: 'callout',
          text: 'ถ้าไม่พบรายชื่อ ให้ลองพิมพ์คำค้นที่ตรงขึ้น (ชื่อ-นามสกุล หรืออีเมลเต็ม) ระบบจะค้นเฉพาะผู้ใช้ที่มีอยู่ใน Entra ของบริษัทเท่านั้น',
          variant: 'info',
        },
        {
          kind: 'image',
          src: '/help/settings-users/02-add-user-directory-search.png',
          caption:
            'เพิ่มผู้ใช้ใหม่ — ช่องค้นหาจาก Entra Directory และรายการผลลัพธ์ (ชื่อ + อีเมล) ให้คลิกเลือก',
        },
      ],
    },
    {
      id: 'add-config',
      heading: '4. เลือกผู้ใช้ และกำหนดบริษัท/บทบาทเริ่มต้น',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อคลิกเลือกผู้ใช้จากผลการค้นหา ระบบจะแสดงการ์ดข้อมูลผู้ใช้ที่เลือก (ชื่อ + อีเมล) จากนั้นกำหนดสิทธิ์การเข้าถึงด้านล่าง',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'บริษัทที่เข้าถึง — ',
              text: 'ติ๊กเลือกบริษัทที่ผู้ใช้เข้าถึงได้ (DCC/TTOP/RCI) ต้องเลือกอย่างน้อย 1 บริษัท',
            },
            {
              lead: 'บทบาทเริ่มต้น (ไม่บังคับ) — ',
              text: 'ติ๊กเลือกบทบาทที่จะกำหนดให้ผู้ใช้ตั้งแต่แรก จะมีให้เลือกเมื่อระบบมีบทบาทตั้งไว้',
            },
          ],
        },
        {
          kind: 'para',
          text: 'เมื่อกรอกครบ (เลือกผู้ใช้ที่มีอีเมล + เลือกบริษัทอย่างน้อย 1) ปุ่ม สร้างผู้ใช้ จะกดได้ กดเพื่อบันทึก ระบบจะปิดหน้าต่างและแสดงผู้ใช้ใหม่ในตาราง หากต้องการยกเลิกให้กด ยกเลิก',
        },
        {
          kind: 'callout',
          text: 'หากผู้ใช้ที่เลือกไม่มีอีเมล ระบบจะแจ้งเตือนสีแดงว่า “ผู้ใช้นี้ไม่มีอีเมล — ไม่สามารถสร้างบัญชีได้” และปุ่มสร้างผู้ใช้จะกดไม่ได้ ให้เลือกผู้ใช้คนอื่นที่มีอีเมลแทน',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/settings-users/03-add-user-selected.png',
          caption:
            'เลือกผู้ใช้แล้ว — การ์ดข้อมูลผู้ใช้ (ชื่อ+อีเมล), ติ๊กบริษัทที่เข้าถึง และบทบาทเริ่มต้น (ไม่บังคับ)',
        },
      ],
    },
    {
      id: 'edit',
      heading: '5. การแก้ไขผู้ใช้',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม แก้ไข ที่แถวของผู้ใช้ ระบบจะเปิดหน้าต่าง “แก้ไขผู้ใช้: {อีเมล}” สำหรับปรับปรุงข้อมูลและสิทธิ์ของผู้ใช้คนนั้น',
        },
        {
          kind: 'bullets',
          items: [
            { lead: 'ชื่อแสดง — ', text: 'แก้ชื่อที่แสดงในระบบ' },
            {
              lead: 'บริษัทที่เข้าถึง — ',
              text: 'ติ๊กเลือกบริษัทที่เข้าถึงได้ (ต้องมีอย่างน้อย 1 บริษัท)',
            },
            {
              lead: 'บริษัทเริ่มต้น — ',
              text: 'เลือกบริษัทที่จะเปิดเป็นค่าเริ่มต้นเมื่อผู้ใช้เข้าระบบ (เลือกได้จากบริษัทที่เข้าถึงได้เท่านั้น)',
            },
            { lead: 'ใช้งาน (Active) — ', text: 'ติ๊กเพื่อให้บัญชีใช้งานได้ ปลดติ๊กเพื่อปิดบัญชี' },
          ],
        },
        {
          kind: 'para',
          text: 'แก้ไขเสร็จกด บันทึก (กดได้เมื่อมีการเปลี่ยนแปลงและเลือกบริษัทไว้อย่างน้อย 1) หรือกด ยกเลิก เพื่อปิดโดยไม่บันทึก',
        },
        {
          kind: 'image',
          src: '/help/settings-users/04-edit-user.png',
          caption:
            'แก้ไขผู้ใช้ — ชื่อแสดง, บริษัทที่เข้าถึง, บริษัทเริ่มต้น และสถานะใช้งาน (Active)',
        },
      ],
    },
    {
      id: 'roles',
      heading: '6. การกำหนดบทบาทเฉพาะบริษัท',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม แก้บทบาท ที่แถวของผู้ใช้ ระบบจะเปิดหน้าต่าง “บทบาทของ {อีเมล}” พร้อมคำอธิบาย “กำหนดบทบาทเฉพาะบริษัทนี้ (บทบาท global จัดการแยก)” จะแสดงรายการบทบาททั้งหมดเป็น checkbox แต่ละรายการมีรหัสบทบาท (ตัวหนา) และชื่อภาษาไทย (สีจาง) บทบาทที่ผู้ใช้มีอยู่แล้วจะถูกติ๊กไว้',
        },
        {
          kind: 'para',
          text: 'ติ๊กหรือปลดติ๊กบทบาทตามต้องการ แล้วกด บันทึก ระบบจะแทนที่บทบาทเฉพาะบริษัทของผู้ใช้ด้วยรายการที่เลือก หรือกด ยกเลิก เพื่อปิดโดยไม่บันทึก',
        },
        {
          kind: 'callout',
          text: 'ปุ่ม “แก้บทบาท” จะแสดงเฉพาะเมื่อกำลังทำงานในบริษัทหนึ่ง และผู้ใช้นั้นอยู่ในบริษัทที่เลือก เพราะบทบาทที่กำหนดผูกกับบริษัทที่กำลังเลือกอยู่ ส่วนบทบาท global เป็นคนละชุดและจัดการแยกต่างหาก',
          variant: 'info',
        },
        {
          kind: 'image',
          src: '/help/settings-users/05-assign-roles.png',
          caption: 'กำหนดบทบาทเฉพาะบริษัทนี้ — รายการบทบาท (รหัส + ชื่อไทย) ติ๊กเลือกแล้วกดบันทึก',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '7. สรุปสถานะของผู้ใช้',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'สี', 'ความหมาย'],
          rows: [
            ['ใช้งาน', 'เขียว', 'บัญชีเปิดใช้งาน เข้าระบบและทำงานได้ตามสิทธิ์ที่กำหนด'],
            ['ปิด', 'แดง', 'บัญชีถูกระงับ ผู้ใช้เข้าระบบไม่ได้ (กด “เปิด” เพื่อคืนสิทธิ์)'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '8. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมพิมพ์อีเมลผู้ใช้ใหม่เองไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ผู้ใช้ทุกคนต้องมีบัญชีใน Microsoft Entra ของบริษัทก่อน จึงต้องค้นหาและเลือกจาก directory เท่านั้น',
            },
          ],
        },
        { kind: 'para', text: 'เพิ่มผู้ใช้แล้วเขายังเข้าระบบไม่ได้ ทำไม?' },
        {
          kind: 'bullets',
          items: [
            { text: 'ตรวจว่าเลือกบริษัทที่เข้าถึงให้แล้วอย่างน้อย 1 บริษัท และสถานะเป็น “ใช้งาน”' },
            {
              text: 'ถ้ายังเข้าได้แต่ทำงานบางเมนูไม่ได้ ให้ตรวจบทบาท (Role) ว่ามีสิทธิ์ที่ต้องการหรือยัง',
            },
          ],
        },
        { kind: 'para', text: 'ปุ่ม “แก้บทบาท” ไม่ขึ้นในบางแถว?' },
        {
          kind: 'bullets',
          items: [
            { text: 'ปุ่มนี้แสดงเมื่อกำลังเลือกบริษัทหนึ่งอยู่ และผู้ใช้นั้นอยู่ในบริษัทที่เลือก' },
          ],
        },
        { kind: 'para', text: 'ต้องการระงับการใช้งานชั่วคราว ทำอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'กดปุ่ม “ปิด” ที่แถวผู้ใช้ (หรือปลดติ๊ก “ใช้งาน” ในหน้าต่างแก้ไข) เมื่อต้องการคืนสิทธิ์ให้กด “เปิด”',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '9. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Entra Directory — ',
              text: 'ไดเรกทอรีบัญชีผู้ใช้ของบริษัทบน Microsoft Entra (อีเมลองค์กร) ใช้เป็นแหล่งผู้ใช้ของระบบ',
            },
            {
              lead: 'บทบาท (Role) — ',
              text: 'ชุดสิทธิ์การใช้งานที่กำหนดให้ผู้ใช้ แทนการตั้งสิทธิ์ทีละเมนู',
            },
            {
              lead: 'บทบาท global — ',
              text: 'บทบาทที่มีผลทุกบริษัทที่ผู้ใช้เข้าถึงได้ (จัดการแยกจากบทบาทเฉพาะบริษัท)',
            },
            {
              lead: 'บทบาทเฉพาะบริษัท — ',
              text: 'บทบาทที่มีผลเฉพาะบริษัทที่กำลังเลือกอยู่ กำหนดผ่านปุ่ม “แก้บทบาท”',
            },
            {
              lead: 'บริษัทที่เข้าถึง — ',
              text: 'รายชื่อบริษัท (DCC/TTOP/RCI) ที่ผู้ใช้มองเห็นและทำงานได้',
            },
            {
              lead: 'บริษัทเริ่มต้น — ',
              text: 'บริษัทที่ระบบเปิดให้อัตโนมัติเมื่อผู้ใช้ล็อกอินเข้าระบบ',
            },
            { lead: 'สถานะ ใช้งาน/ปิด — ', text: 'สวิตช์เปิด-ปิดสิทธิ์เข้าใช้ระบบของบัญชีผู้ใช้' },
          ],
        },
      ],
    },
  ],
};
